class BlankabricMobileMenu extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const {signal} = this.controller;
    this.header = this.closest('.bb-header');
    this.toggle = this.header?.querySelector('[data-bb-mobile-menu-toggle]');
    this.panels = [...this.querySelectorAll('[data-menu-panel]')];
    this.stack = [];
    this.current = this.panels[0];
    this.isOpen = false;
    this.querySelectorAll('[data-menu-next]').forEach(link => {
      link.setAttribute('role', 'button');
      link.setAttribute('aria-expanded', 'false');
    });
    this.addEventListener('click', event => {
      const next = event.target.closest('[data-menu-next]');
      if (next) {
        const panel = this.panels.find(item => item.dataset.menuPanel === next.dataset.menuNext);
        if (!panel) return;
        event.preventDefault();
        this.stack.push({panel:this.current, trigger:next, scroll:this.scrollTop});
        next.setAttribute('aria-expanded', 'true');
        this.show(panel);
        return;
      }
      if (event.target.closest('[data-menu-back]')) this.back();
      else if (event.target.closest('a[href]')) this.close(false);
    }, {signal});
    this.addEventListener('keydown', event => {
      if (event.key === ' ' && event.target.matches('[data-menu-next]')) {
        event.preventDefault();
        event.target.click();
      }
    }, {signal});
    document.addEventListener('keydown', event => {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.stack.length) this.back();
        else this.close(true);
      } else if (event.key === 'Tab') {
        const elements = [this.toggle, ...this.current.querySelectorAll('a[href], button')].filter(Boolean);
        const index = elements.indexOf(document.activeElement);
        if (event.shiftKey && index <= 0) {
          event.preventDefault();
          elements.at(-1)?.focus();
        } else if (!event.shiftKey && (index === elements.length - 1 || index === -1)) {
          event.preventDefault();
          elements[0]?.focus();
        }
      }
    }, {capture:true, signal});
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(document.body, {attributes:true, attributeFilter:['class']});
    window.addEventListener('resize', () => this.sync(), {signal});
    window.visualViewport?.addEventListener('resize', () => this.measure(), {signal});
    this.sync();
  }

  show(panel, focus = true) {
    this.panels.forEach(item => { item.hidden = item !== panel; });
    this.current = panel;
    this.scrollTop = 0;
    if (focus) (panel.querySelector('[data-menu-heading]') || panel.querySelector('a, button'))?.focus();
  }

  back() {
    const previous = this.stack.pop();
    if (!previous) return;
    this.show(previous.panel, false);
    previous.trigger.setAttribute('aria-expanded', 'false');
    previous.trigger.focus();
    this.scrollTop = previous.scroll;
  }

  reset() {
    this.stack = [];
    this.querySelectorAll('[data-menu-next]').forEach(link => link.setAttribute('aria-expanded', 'false'));
    this.show(this.panels[0], false);
  }

  measure() {
    if (!this.isOpen) return;
    const top = this.header.getBoundingClientRect().bottom;
    const viewport = window.visualViewport;
    const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
    this.style.setProperty('--bb-mobile-menu-height', `${Math.max(160, bottom - top)}px`);
  }

  sync() {
    const open = window.matchMedia('(max-width: 899px)').matches && document.body.classList.contains('bb-mobile-megamenu-open');
    if (open !== this.isOpen) {
      this.isOpen = open;
      this.reset();
      if (open) this.current.querySelector('a, button')?.focus();
    }
    this.measure();
  }

  close(focus) {
    document.body.classList.remove('bb-mobile-megamenu-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
    this.reset();
    if (focus) this.toggle?.focus();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.abort();
    this.controller = null;
  }
}

if (!customElements.get('blankabric-mobile-menu')) customElements.define('blankabric-mobile-menu', BlankabricMobileMenu);
